<template>
    <div class="tx-audience-tab">
        <div class="tab-header">
            <h4>选择人群进行绑定</h4>
            <div class="header-actions">
                <el-button type="primary" size="small" @click="fetchAvailableAudiences">
                    <el-icon>
                        <Refresh />
                    </el-icon>
                    刷新列表
                </el-button>
                <el-button type="success" size="small" @click="syncTxAudiences" :loading="syncLoading">
                    <el-icon>
                        <Connection />
                    </el-icon>
                    同步人群
                </el-button>
            </div>
        </div>

        <!-- 搜索和筛选 -->
        <div class="search-section">
            <el-input v-model="searchKeyword" placeholder="搜索人群名称" clearable size="small"
                style="width: 300px; margin-right: 10px" @keyup.enter="searchAudiences">
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>
            <el-button type="primary" size="small" @click="searchAudiences">
                搜索
            </el-button>
        </div>

        <!-- 可绑定人群列表 -->
        <el-table :data="availableAudiences" stripe style="width: 100%" height="300" v-loading="availableLoading"
            @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="55" />
            <el-table-column prop="name" label="人群名称" min-width="200" show-overflow-tooltip />
            <el-table-column label="人群类型" width="120">
                <template #default="{ row }">
                    <el-tag size="small">{{ getAudienceTypeText(row.type) }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="description" label="描述" width="200" show-overflow-tooltip>
                <template #default="{ row }">
                    <span v-if="row.description">{{ row.description }}</span>
                    <span v-else class="empty-value">无描述</span>
                </template>
            </el-table-column>
            <el-table-column label="用户数量" width="100">
                <template #default="{ row }">
                    <span v-if="row.user_count > 0">{{ row.user_count }} 人</span>
                    <span v-else class="empty-value">暂无</span>
                </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
                <template #default="{ row }">
                    <el-tag :type="getStatusTagType(row.status)" size="small">
                        {{ getStatusText(row.status) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="created_time" label="创建时间" width="180" />
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container" v-if="availablePagination.total > 0">
            <el-pagination v-model:current-page="availablePagination.page"
                v-model:page-size="availablePagination.pageSize" :total="availablePagination.total"
                :page-sizes="[5, 10, 20]" layout="total, sizes, prev, pager, next, jumper"
                @size-change="fetchAvailableAudiences" @current-change="fetchAvailableAudiences" />
        </div>

        <!-- 绑定按钮 -->
        <div class="bind-actions" v-if="selectedAudiences.length > 0">
            <el-alert :title="`已选择 ${selectedAudiences.length} 个人群`" type="info" :closable="false"
                style="margin-bottom: 10px" />
            <el-button type="primary" @click="bindSelectedAudiences" :loading="bindLoading">
                <el-icon>
                    <Connection />
                </el-icon>
                绑定选中人群
            </el-button>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Refresh, Connection, Search } from '@element-plus/icons-vue';

export default {
    name: 'TxAudienceTab',
    components: {
        Refresh, Connection, Search
    },
    props: {
        card: {
            type: Object,
            required: true
        },
        bindType: {
            type: String,
            required: true,
            validator: (value) => ['openid', 'mobile'].includes(value)
        },
        platform: {
            type: String,
            default: 'tx'
        }
    },
    data() {
        return {
            searchKeyword: '',
            availableAudiences: [],
            availableLoading: false,
            selectedAudiences: [],
            syncLoading: false,
            bindLoading: false,
            availablePagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        };
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('pc_user_info') || '{}');
        }
    },

    mounted() {
        this.fetchAvailableAudiences();
    },
    methods: {
        // 获取可绑定人群
        async fetchAvailableAudiences() {
            this.availableLoading = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/business_card_management.php`,
                    {
                        action: 'get_available_audiences',
                        store_id: this.userInfo.store_id,  // 使用 store_id
                        user_id: this.userInfo.id,  // 使用 user_id
                        card_id: this.card.id,
                        platform: this.platform,
                        bind_type: this.bindType,
                        page: this.availablePagination.page,
                        page_size: this.availablePagination.pageSize,
                        keyword: this.searchKeyword || undefined
                    }
                );

                if (response.data.data) {
                    this.availableAudiences = response.data.data;
                    this.availablePagination.total = response.data.pagination?.total || 0;
                } else if (response.data.error) {
                    this.$message.warning(response.data.error);
                    this.availableAudiences = [];
                }
            } catch (error) {
                console.error('获取可绑定人群错误:', error);
                this.availableAudiences = [];
                this.$message.error('获取人群列表失败');
            } finally {
                this.availableLoading = false;
            }
        },

        // 搜索人群
        searchAudiences() {
            this.availablePagination.page = 1;
            this.fetchAvailableAudiences();
        },

        // 同步腾讯人群
        async syncTxAudiences() {
            if (!this.card.gzh_id) {
                this.$message.warning('当前名片未关联TX广告授权，无法同步人群');
                return;
            }

            try {
                this.syncLoading = true;

                // 需要先获取广告商账户信息
                // 调用接口获取该授权下的广告商列表
                const accountsResponse = await axios.post(
                    `${config.BASE_URL}/pc/api/advertiser_sync.php`,
                    {
                        action: 'list',
                        store_id: this.userInfo.store_id,  // 使用 store_id
                        user_id: this.userInfo.id,  // 使用 user_id
                    }
                );

                if (!accountsResponse.data.data || accountsResponse.data.data.length === 0) {
                    this.$message.warning('未找到广告商账户，请先创建广告商');
                    return;
                }

                // 找到当前授权对应的广告商
                const authAccounts = accountsResponse.data.data.filter(account =>
                    account.auth_id == this.card.gzh_id
                );

                if (authAccounts.length === 0) {
                    this.$message.warning('未找到当前授权对应的广告商账户');
                    return;
                }

                // 如果有多个账户，让用户选择
                let accountId;
                if (authAccounts.length > 1) {
                    // 这里可以添加一个选择弹窗
                    // 暂时选择第一个
                    accountId = authAccounts[0].account_id;
                } else {
                    accountId = authAccounts[0].account_id;
                }

                // 调用同步接口
                const response = await axios.post(
                    `${config.BASE_URL}/pc/api/custom_audience_sync.php`,
                    {
                        action: 'sync_audiences',
                        store_id: this.userInfo.store_id,  // 使用 store_id
                        user_id: this.userInfo.id,  // 使用 user_id
                        auth_id: this.card.gzh_id,
                        account_id: accountId  // 添加 account_id 参数
                    }
                );

                if (response.data.success) {
                    this.$message.success('人群同步成功');
                    this.fetchAvailableAudiences();
                    this.$emit('refresh');
                } else {
                    this.$message.error(response.data.error || '同步失败');
                }
            } catch (error) {
                console.error('同步人群错误:', error);
                this.$message.error('同步失败');
            } finally {
                this.syncLoading = false;
            }
        },

        // 选择变化处理
        handleSelectionChange(selection) {
            this.selectedAudiences = selection;
        },

        // 绑定选中人群
        async bindSelectedAudiences() {
            if (this.selectedAudiences.length === 0) {
                this.$message.warning('请先选择要绑定的人群');
                return;
            }

            try {
                this.bindLoading = true;
                const promises = [];

                for (const audience of this.selectedAudiences) {
                    const promise = axios.post(
                        `${config.BASE_URL}/pc/api/business_card_management.php`,
                        {
                            action: 'bind_audience',
                            store_id: this.userInfo.store_id,  // 使用 store_id
                            user_id: this.userInfo.id,  // 使用 user_id
                            card_id: this.card.id,
                            audience_id: audience.audience_id,
                            audience_name: audience.name,
                            platform: this.platform,
                            bind_type: this.bindType,
                            description: `绑定到名片: ${this.card.name}`
                        }
                    );
                    promises.push(promise);
                }

                const results = await Promise.all(promises);
                let successCount = 0;
                let failCount = 0;

                results.forEach((result, index) => {
                    if (result.data.success) {
                        successCount++;
                    } else {
                        failCount++;
                        console.error(`绑定人群 ${this.selectedAudiences[index].name} 失败:`, result.data.error);
                    }
                });

                if (successCount > 0) {
                    this.$message.success(`成功绑定 ${successCount} 个人群`);
                    this.selectedAudiences = [];
                    this.fetchAvailableAudiences();
                    this.$emit('refresh');
                }

                if (failCount > 0) {
                    this.$message.warning(`${failCount} 个人群绑定失败，请重试`);
                }
            } catch (error) {
                console.error('绑定人群错误:', error);
                this.$message.error('绑定失败');
            } finally {
                this.bindLoading = false;
            }
        },

        // 工具函数
        getAudienceTypeText(type) {
            const textMap = {
                'CUSTOMER_FILE': '客户文件',
                'LOOKALIKE': 'Lookalike',
                'USER_ACTION': '用户行为',
                'KEYWORD': '关键词',
                'COMBINE': '组合'
            };
            return textMap[type] || type;
        },

        getStatusTagType(status) {
            const typeMap = {
                'SUCCESS': 'success',
                'PROCESSING': 'warning',
                'PENDING': 'info',
                'ERROR': 'danger',
                'DELETED': 'danger'
            };
            return typeMap[status] || 'info';
        },

        getStatusText(status) {
            const textMap = {
                'SUCCESS': '活跃',
                'PROCESSING': '处理中',
                'PENDING': '等待中',
                'ERROR': '错误',
                'DELETED': '已删除'
            };
            return textMap[status] || status;
        }
    }
};
</script>

<style scoped lang="scss">
.tx-audience-tab {
    .tab-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 1px solid #ebeef5;

        h4 {
            margin: 0;
            color: #1f2937;
            font-weight: 600;
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .search-section {
        margin-bottom: 20px;
        padding: 15px;
        background: #f8fafc;
        border-radius: 8px;
        display: flex;
        align-items: center;
    }

    .bind-actions {
        margin-top: 20px;
        padding: 15px;
        background: #f0f9ff;
        border: 1px solid #e1f5fe;
        border-radius: 8px;
    }
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.pagination-container {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
}

@media (max-width: 768px) {
    .search-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;

        .el-input {
            width: 100% !important;
        }
    }
}
</style>